import { base64SvgToSrc } from './utils.ts';
import { useEffect, useRef, useState, ChangeEvent } from 'react';
import { translations } from '../translations/translations';
import { Action, Connector, Trigger } from '../tasks/types';
import { ExInput, ExButton, ExEmptyState } from '@boomi/exosphere';

import './selector.css';

type TaskItemOption = { label: string; value: Action | Trigger };

interface Props {
    connector: Connector;
    itemType: 'action' | 'trigger';
    onSelect: (option: TaskItemOption | null) => void;
    language: string;
}

const Selector = ({ connector, itemType, onSelect, language = 'en' }: Props) => {
    const selectorRef = useRef<HTMLDivElement>(null);
    const [search, setSearch] = useState('');

    const options = Object.values(
        itemType === 'action' ? connector!.actions : connector!.triggers,
    ).map((item) => ({
        label: item.name,
        value: item,
    }));

    const filteredOptions = options.filter((option) =>
        option.label.toUpperCase().includes(search.toUpperCase()),
    );

    useEffect(() => {
        const { current: selector } = selectorRef;

        // Set up the intersection observer. Using it to figure out when the header is at top edge.
        const intersectionObserver = new IntersectionObserver(
            ([entry]) => entry.target.classList.toggle('is-at-top', entry.isIntersecting),
            {
                threshold: 0,
                rootMargin: '0px 0px -100% 0px',
                root: document.querySelector('.automate-content'),
            },
        );

        selector && intersectionObserver.observe(selector);

        return () => {
            selector && intersectionObserver.unobserve(selector);
        };
    }, []);

    //  If searching scroll the options back into view so the results are fully visible.
    useEffect(() => {
        if (!!search && filteredOptions.length && selectorRef?.current) {
            selectorRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    }, [search, filteredOptions.length]);

    return (
        <section className="selector" ref={selectorRef}>
            <header className="selector-header">
                <p className="selector-hint text lg">
                    <img
                        className="icon"
                        src={base64SvgToSrc(connector.icon)}
                        alt={connector.displayName}
                    />
                    {translations[language].TASK_select} {connector.displayName}{' '}
                    {itemType === 'trigger'
                        ? translations[language].TASK_item_type_trigger
                        : translations[language].TASK_item_type_action}
                </p>
                <ExInput
                    data-testId="selectorSearchInput"
                    className="exo-search-input"
                    placeholder={translations[language].COMMON_search_placeholder}
                    input-id="default"
                    type="text"
                    footertype="info"
                    inputmode="text"
                    size="default"
                    flavor="default"
                    leadingicon="magnifying-glass"
                    removemarginbottom
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setSearch(e.currentTarget.value)
                    }
                />
            </header>

            <div className="selector-content" data-testid={`${itemType}_selector`}>
                {filteredOptions && filteredOptions?.length ? (
                    filteredOptions // sort by label A-Z
                        .sort((a, b) => {
                            const labelA = a.label.toUpperCase();
                            const labelB = b.label.toUpperCase();
                            if (labelA < labelB) {
                                return -1;
                            }
                            if (labelA > labelB) {
                                return 1;
                            }

                            // labels are equal (should not really happen)
                            return 0;
                        })
                        .map((option, index) => (
                            <ExButton
                                key={index}
                                role="button"
                                className="exo-button"
                                flavor="branded"
                                size="large"
                                type="secondary"
                                onClick={() => onSelect(option)}
                            >
                                {option.label}
                            </ExButton>
                        ))
                ) : (
                    <ExEmptyState
                        className="empty-state"
                        icon="community"
                        label={translations[language].COMMON_no_results}
                        text={translations[language].COMMON_search_hint}
                    />
                )}
            </div>
        </section>
    );
};
export default Selector;
